import { InfraredFlight } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateInfraredFlightArgs,
    getDefaultInfraredFlightCreateArgs,
    UpdateInfraredFlightArgs,
} from "@nova/serializer/src/client/validators/flights/infrared"
import {
    selectInfraredFlightById,
    selectInfraredFlights,
} from "features/entity-caching/flights/selectors"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { updateIntegrationMetadata } from "features/pubsub/module"
import { setFlightId } from "features/map/control/slice"
import { deleteUploadSet } from "plugins/InfraredImage/InfraredImageSlice"
import { clearFlightCsv } from "features/job-management/uploader/mma-telemetry"
import { SampleDataFlightKey } from "@nova/constants"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectSingleLayer,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

const {
    create: createInfraredFlight,
    update: updateInfraredFlight,
    delete: deleteInfraredFlight,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    InfraredFlight, // entity type
    CreateInfraredFlightArgs, // create args to send to server
    ["createdBy", "perimeterDistance"], // create args required by action
    UpdateInfraredFlightArgs, // update args,
    {
        updateIntegrationId?: string
        sampleDataFlightKey?: SampleDataFlightKey
        name?: string
        skippedProcessing?: boolean
    }, // required input context
    { sampleDataFlightKey?: SampleDataFlightKey }
>("InfraredFlight")

export const addInfraredFlightsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const distanceUnit = selectWorkspaceDistanceUnit(state)
            const flights = selectInfraredFlights(state)
            const name = context?.name || `Hotspots ${Object.values(flights).length + 1}`
            const id = randomUUID()
            const args = {
                name,
                skippedProcessing: !!context?.skippedProcessing,
                id,
                demoId: !!context?.sampleDataFlightKey ? "doesnt-matter" : undefined,
            }
            const optimisticData = {
                ...args,
                ...getDefaultInfraredFlightCreateArgs(state.project.data),
                ...data,
            }
            optimisticData.perimeterDistance = {
                ...optimisticData.perimeterDistance,
                unit: distanceUnit,
            }
            createInfraredFlight(
                socket,
                {
                    ...args,
                    perimeterDistance: { range: [], unit: distanceUnit },
                    ...data,
                },
                { sampleDataFlightKey: context?.sampleDataFlightKey }
            )
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["infraredFlights"],
                })
            )

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
            store.dispatch(setFlightId(id))
            store.dispatch(setActiveLayerId({ id: optimisticData.id }))
            store.dispatch(setActiveFeatureId({ feature: NovaPluginIds.IR }))
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateInfraredFlight(socket, id, updates, { ...context })

            if (context?.updateIntegrationId) {
                const state = store.getState()
                const entity = selectInfraredFlightById(id)(state)!
                if (entity) {
                    updates.integrations = updateIntegrationMetadata(
                        entity.integrations,
                        context.updateIntegrationId
                    )
                }
            }

            if (context?.sampleDataFlightKey) updates.demoId = "doesnt-matter"

            store.dispatch(updateEntitySocket({ updates, path: ["infraredFlights", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            const dispatch = store.dispatch
            dispatch(deleteUploadSet({ flightId: id }))
            clearFlightCsv(id)
            deleteInfraredFlight(socket, id, {})
            dispatch(deleteEntitySocket({ path: ["infraredFlights", { id }] }))
        })

export {
    createAction as createInfraredFlight,
    updateAction as updateInfraredFlight,
    deleteAction as deleteInfraredFlight,
}
